import type { ReactNode } from "react";
import { Link, useInRouterContext, useLocation } from "react-router-dom";
import { Search } from "lucide-react";
import { useAgentResearchSummary } from "./useAgentResearch";

/** The workspace's address, with the run when there is one to open on. */
export function agentResearchHref(listingId: string, runId?: string | null): string {
  const base = `/listings/${listingId}/agent-research`;
  return runId ? `${base}?run=${encodeURIComponent(runId)}` : base;
}

/**
 * R6 — from the listing's own page the workspace opens in the SAME tab (it has a
 * back link); from anywhere else — this body inside a record drawer over a
 * buyer's shortlist or a submission — in a NEW tab, so the host page, its drawer
 * and the reader's place are not unmounted (the `BrokerLeadLink` rule).
 */
interface WorkspaceLinkProps {
  listingId: string;
  to: string;
  className: string;
  testId: string;
  children: ReactNode;
}

function NewTabLink({ to, className, testId, children }: Omit<WorkspaceLinkProps, "listingId">) {
  return (
    <a href={to} target="_blank" rel="noopener noreferrer" className={className} data-testid={testId}>
      {children}
    </a>
  );
}

function RoutedWorkspaceLink(props: WorkspaceLinkProps) {
  const { pathname } = useLocation();
  const onListingPage = pathname.replace(/\/+$/, "") === `/listings/${props.listingId}`;
  return onListingPage ? (
    <Link to={props.to} className={props.className} data-testid={props.testId}>
      {props.children}
    </Link>
  ) : (
    <NewTabLink {...props} />
  );
}

/** Outside a router (an isolated host) there is no page to stay on: a new tab. */
function WorkspaceLink(props: WorkspaceLinkProps) {
  return useInRouterContext() ? <RoutedWorkspaceLink {...props} /> : <NewTabLink {...props} />;
}

/**
 * The Listing agent card's door into research (D7, D18). FIND AGENT when no agent
 * is attached, RESEARCH when one is — Listings.Manage only, and LINKS, never
 * mutations: the card sits on an Overview, which may link but carries no
 * workflow action. Under it, for every reader, "Research in progress" or
 * "Research: N findings to review", linking to the run.
 */
export function AgentResearchEntry({
  listingId,
  hasAgent,
  canManage,
}: {
  listingId: string;
  hasAgent: boolean;
  canManage: boolean;
}) {
  const summary = useAgentResearchSummary(listingId);
  const s = summary.data;
  const runId = s?.activeRunId ?? s?.latestRunId ?? null;

  const line = s?.activeRunId
    ? "Research in progress"
    : s && s.findingsToReview > 0
      ? `Research: ${s.findingsToReview} ${s.findingsToReview === 1 ? "finding" : "findings"} to review`
      : null;

  if (!canManage && !line) return null;

  return (
    <div className="flex flex-wrap items-center gap-space-3" data-testid="agent-research-entry">
      {canManage && (
        <WorkspaceLink
          listingId={listingId}
          to={agentResearchHref(listingId)}
          className="flex items-center gap-space-1 text-small text-gold-shadow hover:underline"
          testId={hasAgent ? "agent-research-entry-research" : "agent-research-entry-find"}
        >
          <Search size={13} strokeWidth={1.5} aria-hidden /> {hasAgent ? "Research" : "Find agent"}
        </WorkspaceLink>
      )}
      {line && (
        <WorkspaceLink
          listingId={listingId}
          to={agentResearchHref(listingId, runId)}
          className="text-small text-gold-shadow hover:underline"
          testId="agent-research-entry-line"
        >
          {line}
        </WorkspaceLink>
      )}
    </div>
  );
}

/** FIND AGENT beside + NEW AGENT and PASTE TO FILL in the Edit listing agent
 *  modal — a link to the workspace (R6's tab rule), never a write. */
export function FindAgentLink({ listingId }: { listingId: string }) {
  return (
    <WorkspaceLink
      listingId={listingId}
      to={agentResearchHref(listingId)}
      className="inline-flex items-center gap-space-1 rounded-md border-[1.5px] border-gold px-space-3 py-space-1 text-small text-onyx hover:bg-gold-highlight/10"
      testId="listing-agent-find-agent"
    >
      <Search size={14} strokeWidth={1.5} aria-hidden /> Find agent
    </WorkspaceLink>
  );
}
